import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

// ─── Calculation constants ───────────────────────────────────────────────────
const STROOMPRIJS = 0.32;       // €/kWh all-in
const TERUGLEVER = 0.05;        // €/kWh feed-in after 1-1-2027
const OPWEK_PANEEL = 350;       // kWh/year per panel
const TERUGLEVERDEEL = 0.65;    // share fed back without battery
const CYCLI_JAAR = 220;         // useful full cycles/year
const DYN_BONUS = 60;           // €/kWh capacity/year (dynamic only)
const CAP_GRAFEEN = 11.2;       // kWh
const CAP_LITHIUM = 10.24;      // kWh
const PRIJS_GRAFEEN = 9500;     // € placeholder — update when confirmed
const PRIJS_LITHIUM = 7500;     // € placeholder — update when confirmed

const roundTo25 = (n) => Math.round(n / 25) * 25;

const SavingsCalculator = () => {
  const [panels, setPanels] = useState('ja');
  const [panelCount, setPanelCount] = useState(10);
  const [usage, setUsage] = useState(3500);
  const [contract, setContract] = useState('dynamisch');
  const [battery, setBattery] = useState('grafeen');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const capaciteit = battery === 'grafeen' ? CAP_GRAFEEN : CAP_LITHIUM;
  const prijs = battery === 'grafeen' ? PRIJS_GRAFEEN : PRIJS_LITHIUM;
  const isGrafeen = battery === 'grafeen';

  const calc = () => {
    const opwek = panels === 'ja' ? panelCount * OPWEK_PANEEL : 0;
    const teruglevering = opwek * TERUGLEVERDEEL;
    const verschoven_kWh = Math.min(teruglevering, capaciteit * CYCLI_JAAR);
    const zelfverbruik_eur = verschoven_kWh * (STROOMPRIJS - TERUGLEVER); // × 0.27
    const dynamisch_eur = contract === 'dynamisch' ? capaciteit * DYN_BONUS : 0;
    const jaarbesparing = zelfverbruik_eur + dynamisch_eur;

    // Payback bandwidth
    const paybackLow = jaarbesparing > 0 ? Math.round(prijs / (jaarbesparing * 1.15)) : null;
    const paybackHigh = jaarbesparing > 0 ? Math.round(prijs / (jaarbesparing * 0.90)) : null;

    // Dynamic scenario for nudge (fixed + panels)
    const dynamisch_eur_scenario = capaciteit * DYN_BONUS;
    const jaarbesparing_dynamisch = roundTo25(zelfverbruik_eur + dynamisch_eur_scenario);

    return {
      jaarbesparing: roundTo25(jaarbesparing),
      paybackLow,
      paybackHigh,
      jaarbesparing_dynamisch,
      verschoven_kWh: Math.round(verschoven_kWh),
    };
  };

  const { jaarbesparing, paybackLow, paybackHigh, jaarbesparing_dynamisch } = calc();

  // Edge case: fixed contract + no solar panels
  const isEdgeCaseNoResult = panels === 'nee' && contract === 'vast';
  // Edge case: payback > 15 years
  const isEdgeCaseLongPayback = !isEdgeCaseNoResult && paybackHigh !== null && paybackHigh > 15;
  // Edge case: fixed contract + solar panels → nudge
  const showDynamicNudge = panels === 'ja' && contract === 'vast' && !isEdgeCaseLongPayback;

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="rekenhulp" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 text-primary bg-primary/10 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider mb-4">
            <Icon name="Calculator" size={12} />
            <span>Besparingsindicatie</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground leading-tight">
            Wat levert een thuisbatterij jóú op?
          </h2>
          <p className="mt-4 font-body text-muted-foreground">
            Vul je situatie in en zie direct een indicatie van je jaarlijkse besparing.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

            {/* ── Inputs ─────────────────────────────────────────────────── */}
            <div className="lg:col-span-3 bg-gray-50 border border-border rounded-2xl p-7 space-y-6">

              {/* Zonnepanelen toggle */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                  Heb je zonnepanelen?
                </label>
                <div className="flex gap-3">
                  {[{ val: 'ja', label: 'Ja' }, { val: 'nee', label: 'Nee' }]?.map((opt) => (
                    <button
                      key={opt?.val}
                      onClick={() => setPanels(opt?.val)}
                      className={`flex-1 py-2.5 text-sm font-heading font-semibold rounded-md border transition-colors ${
                        panels === opt?.val
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white text-foreground border-border hover:border-primary/50'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Aantal panelen */}
              {panels === 'ja' && (
                <div>
                  <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                    Aantal zonnepanelen: <span className="text-primary">{panelCount}</span>
                  </label>
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={panelCount}
                    onChange={(e) => setPanelCount(Number(e?.target?.value))}
                    className="w-full accent-primary"
                  />
                  <div className="flex justify-between text-xs font-caption text-muted-foreground mt-1">
                    <span>4</span><span>24</span>
                  </div>
                </div>
              )}

              {/* Jaarverbruik */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                  Jaarverbruik: <span className="text-primary">{usage?.toLocaleString('nl-NL')} kWh</span>
                </label>
                <input
                  type="range"
                  min="1500"
                  max="8000"
                  step="100"
                  value={usage}
                  onChange={(e) => setUsage(Number(e?.target?.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-xs font-caption text-muted-foreground mt-1">
                  <span>1.500 kWh</span><span>8.000 kWh</span>
                </div>
              </div>

              {/* Energiecontract */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                  Energiecontract
                </label>
                <div className="flex gap-3">
                  {[{ val: 'dynamisch', label: 'Dynamisch' }, { val: 'vast', label: 'Vast tarief' }]?.map((opt) => (
                    <button
                      key={opt?.val}
                      onClick={() => setContract(opt?.val)}
                      className={`flex-1 py-2.5 text-sm font-heading font-semibold rounded-md border transition-colors ${
                        contract === opt?.val
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white text-foreground border-border hover:border-primary/50'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Batterijtype */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                  Batterijtype
                </label>
                <div className="flex gap-3">
                  {[
                    { val: 'grafeen', label: 'GRP Graphene (11,2 kWh)' },
                    { val: 'lithium', label: 'GRP Lithium (10,24 kWh)' },
                  ]?.map((opt) => (
                    <button
                      key={opt?.val}
                      onClick={() => setBattery(opt?.val)}
                      className={`flex-1 py-2.5 text-sm font-heading font-semibold rounded-md border transition-colors ${
                        battery === opt?.val
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white text-foreground border-border hover:border-primary/50'
                      }`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Output ─────────────────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-4">

              {/* Result card */}
              <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-7 text-primary-foreground">
                {isEdgeCaseNoResult ? (
                  /* Edge case: fixed + no panels */
                  (<div>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Info" size={18} className="opacity-80 flex-shrink-0" />
                      <p className="text-sm font-heading font-semibold opacity-90">Maatwerk nodig</p>
                    </div>
                    <p className="text-sm leading-relaxed opacity-90">
                      In jouw situatie hangt de besparing sterk af van je tarieven. Een dynamisch contract maakt een batterij het meest rendabel — in het gratis adviesgesprek rekenen we het exact voor je door.
                    </p>
                  </div>)
                ) : isEdgeCaseLongPayback ? (
                  /* Edge case: payback > 15 years */
                  (<div>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Info" size={18} className="opacity-80 flex-shrink-0" />
                      <p className="text-sm font-heading font-semibold opacity-90">Maatwerk nodig</p>
                    </div>
                    <p className="text-sm leading-relaxed opacity-90">
                      Jouw situatie vraagt maatwerk. We rekenen het graag exact voor je door — inclusief het effect op je energielabel en woningwaarde.
                    </p>
                  </div>)
                ) : (
                  /* Normal result */
                  (<>
                    <p className="text-sm font-heading font-semibold opacity-80 mb-1">Naar verwachting bespaar je</p>
                    <p className="text-5xl font-heading font-black mb-1">
                      €{jaarbesparing?.toLocaleString('nl-NL')}
                    </p>
                    <p className="text-sm opacity-70 mb-5">per jaar</p>
                    <div className="pt-5 border-t border-white/20">
                      <p className="text-sm font-heading font-semibold opacity-80 mb-1">Terugverdientijd</p>
                      {paybackLow !== null && paybackHigh !== null ? (
                        <>
                          <p className="text-3xl font-heading font-bold">
                            {paybackLow} à {paybackHigh} jaar
                          </p>
                          <p className="text-xs opacity-70 mt-1">— en gaat daarna nog jaren door.</p>
                          {isGrafeen && (
                            <p className="text-xs opacity-80 mt-2 italic">
                              Met 20.000 laadcycli is de batterij ontworpen om die periode ruimschoots te overleven.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-2xl font-heading font-bold">Op aanvraag</p>
                      )}
                    </div>
                  </>)
                )}
              </div>

              {/* Property value layer (only when panels = ja and no edge case) */}
              {panels === 'ja' && !isEdgeCaseNoResult && !isEdgeCaseLongPayback && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="Home" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-heading font-semibold text-amber-800 mb-1">Extra: woningwaarde</p>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        Sinds 29 mei 2026 telt een thuisbatterij (≥5 kWh, gecombineerd met zonnepanelen) mee voor je energielabel. Eén labelstap staat gemiddeld voor <strong>€7.000–11.000 meer woningwaarde</strong> op een woning van €450.000.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Dynamic nudge (fixed contract + panels) */}
              {showDynamicNudge && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="Zap" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      <strong>Tip:</strong> met een dynamisch contract kan je besparing oplopen tot{' '}
                      <strong>€{jaarbesparing_dynamisch?.toLocaleString('nl-NL')} per jaar</strong>.
                    </p>
                  </div>
                </div>
              )}

              {/* Email capture / CTA */}
              {!submitted ? (
                <form onSubmit={handleSubmit} className="bg-gray-50 border border-border rounded-xl p-5 space-y-3">
                  <p className="text-sm font-heading font-semibold text-foreground">
                    Ontvang je berekening + persoonlijk advies
                  </p>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="jouw@email.nl (optioneel)"
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-heading font-semibold rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Plan een gratis adviesgesprek
                  </button>
                  <p className="text-xs font-caption text-muted-foreground">Geen spam. Je kunt je altijd afmelden.</p>
                </form>
              ) : (
                <div className="bg-success/10 border border-success/20 rounded-xl p-5 text-center">
                  <Icon name="CheckCircle" size={24} className="text-success mx-auto mb-2" />
                  <p className="text-sm font-heading font-semibold text-foreground">Bedankt! We nemen snel contact op.</p>
                </div>
              )}

              {/* Mandatory disclaimer */}
              <p className="text-xs font-caption text-muted-foreground italic leading-relaxed">
                Dit is een indicatie op basis van gemiddelde tarieven en marktgegevens (2026). De werkelijke besparing hangt af van je verbruik(spatroon), energietarieven, de ontwikkeling van de energiemarkt en je installatie. Opbrengsten uit de onbalansmarkt kunnen jaarlijks variëren. Aan deze berekening kunnen geen rechten worden ontleend; in het adviesgesprek maken we een berekening op maat.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
